import _ from 'lodash'
import Backbone from 'backbone'
import template from './repeat-by-form.hbs'
import subtotalListRow from './subtotal-list-row.hbs'
import StackForm from './stack-form'
import i18n from 'scoreboard/components/i18n'
import attachFieldDropdown from './attach-field-dropdown'
import ReportBreakdown from './model/designer-breakdown'
import CalendarRangeSelector from 'scoreboard/components/calendar-range-selector'
import dropdown from 'scoreboard/components/dropdown'
import { defaultRange }
	from 'scoreboard/reports/scorecard-report/scorecard-report-model'
import OnOffToggle from 'scoreboard/components/on-off-toggle'
import Tooltip from 'scoreboard/components/tooltip'
import SubtotalMenu from './subtotal-menu'
import calendars from 'scoreboard/components/calendars'
import CovertButtonTooltips from 'scoreboard/components/covert-button-tooltips'
import SummaryType from 'scoreboard/datasets/model/summary-type'
const { VALUE, RANGE } = SummaryType

/**
 * Returns true if this breakdown is a scored scorecard field, as denoted by the
 * breakdown specifying a range but not a field to breakdown by.
 */
function isBreakdownByCalendarPeriod(reportModel, breakdown, targetColumn) {
	// with initiative reports, it's only broken down by calendar periods if
	// that was exclusively picked. if a column was picked, the column id is the
	// 'breakdownBy' property, so we can know that this is the calendar period
	// option if there is no 'breakdownBy'
	if (reportModel.get('subtype') == 'initiative-report-designer') {
		return !breakdown.has('breakdownBy')
	}

	if (breakdown.isTimeBasedBreakdown(reportModel)) {
		return true
	}
	if (targetColumn) {
		return reportModel.columnDataByFieldIdOrColumnType(targetColumn.get('columnType')).changesOverTime
	}
	return false
}

function defaultBreakdownAttributes({ columnData, breakdownBy, ranges }) {
	let summaryType = VALUE
	let { range, lastRange } = ranges
	if (!_.isNil(range) || columnData.changesOverTime) {
		range = lastRange || range
		summaryType = RANGE
	}

	return { breakdownBy, range, summaryType }
}

const RepeatByForm = StackForm.extend({

	className: 'repeat-by-form report',

	events: {
		'change select[name=fieldIdOrColumnType]': '_onChangeFieldIdOrColumnType',
		'change select[name=summaryType]': '_onChangeSummaryType',
		'click .yearly-summary-toggle .on-off-toggle ': '_onClickYearlySummaryToggle',
		'change [name=yearlySummary]': '_onChangeYearlySummary',
		'click .subtotals .add' : '_onClickSubtotalAdd',
		'click .subtotals .delete' : '_onClickSubtotalDelete'
	},

	/**
	 * View for adding detailed or editing the repeat-by column of a designer report
	 *
	 * @param {ReportStack} options.model the Report Stack
	 * @param {ReportModel} options.reportModel the model of the designer
	 * @param {ReportTableModel} options.tableModel the model of the table
	 * @param {ReportColumn} options.column the model of one of a column in a stack
	 * @param {Object|ReportBreakdown} options.breakdown the model (or object) of breakdown (what we're repeating by)
	 */
	initialize: function(options = {}) {
		StackForm.prototype.initialize.apply(this, arguments)
		this.dataset = this.options.dataset
		this.optionData = this.options.optionData
		if (this.options.breakdown instanceof Backbone.Model) {
			this.breakdown = this.options.breakdown.clone()
		} else {
			this.breakdown = new ReportBreakdown(this.options.breakdown)
		}
		this.fieldIdOrColumnType = this.breakdown.get('breakdownBy') || _.get(this, 'optionData[0].id')
		this._lastRange = this.breakdown.get('range')

		this.listenTo(this.breakdown, 'change:breakdownBy', this._onChangeBreakdownBy.bind(this))
	},

	render: function() {
		let dialogTitle = _.isNil(this.options.breakdown) ? 'report.designer.addRepeatingColumns' :
			'report.designer.editRepeatingColumns'
		const doneText = _.isNil(this.options.breakdown) ?
			'datasets.breakdown.columns.show' : 'common.done'
		this.$el.html(template({
			dialogTitle,
			doneText
		}))

		this._renderFieldSelector()
		this._renderCatOrRangeSelector()
		this._renderCalendarRangeSelectorAndSubtotals()
		// this._renderYearlySummaryToggle()	//will be brought back in #20269
		this.covertButtonTooltips = new CovertButtonTooltips({ el: this.el })

		return StackForm.prototype.render.apply(this, arguments)
	},

	_renderFieldSelector: function() {
		//if this breakdown specifies a field/column then this is a repeating column
		//breakdown, add in a dropdown to allow chaning the field/column
		if (this.breakdown.has('breakdownBy')) {
			let { dataset, optionData, fieldIdOrColumnType } = this
			let selectedId = fieldIdOrColumnType
			if (dataset) {
				//if this is a dataset report, ensure we use the idsToFieldIdentifier
				//so that the correct option is set
				selectedId = this.reportModel.fieldIdToOptionId(fieldIdOrColumnType)
			}
			attachFieldDropdown({
				el: this.$('select[name=fieldIdOrColumnType]'),
				selectedId,
				dataset, optionData
			})
		} else {
			this.$('.field').addClass('is-not-displayed')
		}
	},

	_renderCalendarRangeSelectorAndSubtotals: function() {
		_.invoke(this.calendarRangeSelector, 'remove')
		//check to see if the breakdown is time based
		let show = isBreakdownByCalendarPeriod(this.reportModel,
			this.breakdown, this.column)
		if (show) {
			let range = this._lastRange || defaultRange()
			this.calendarRangeSelector = new CalendarRangeSelector({
				canChangeCalendar: true,
				range
			})
			this.$('.calendar-period-selector').append(this.calendarRangeSelector.el)
			this.calendarRangeSelector.render()
			this.calendarRangeSelector.on('change', val => {
				this._lastRange = val
				this.breakdown.set('range', val)
				this._renderSubtotals()
			})

			//rebuild the subtotals if any
			this._renderSubtotals()
		} else {
			delete this.calendarRangeSelector
			this.breakdown.unset('range')
		}

		this.$('.calendar, .subtotals').toggleClass('is-not-displayed', !show)
	},

	_renderCatOrRangeSelector: function() {
		const catOrRangeEl = this.$('.cat-or-range')
		catOrRangeEl.addClass('is-not-displayed')
		let breakdownByCalendarPeriod = isBreakdownByCalendarPeriod(this.reportModel,
			this.breakdown, this.column)  //#40069
		if (!this.dataset || breakdownByCalendarPeriod) { return }

		let optionData = this.reportModel.getSummaryTypeOptionsList(this.breakdown)
		let show = optionData.length > 1
		if (show) {
			let select = this.$('select[name=summaryType]').empty()
			dropdown.idLabels(select, optionData, this.breakdown.get('summaryType'))
		}
		catOrRangeEl.toggleClass('is-not-displayed', !show)
	},

	_renderYearlySummaryToggle: function() {
		if (this.options.reportModel.reportDomainType == 'scorecard-item') {
			this.yearlySummaryToggle = new OnOffToggle({
				name: 'yearlySummary',
				checked: this.breakdown.get('yearlySummary') || false,
				disabled: true
			})

			this.$('.yearly-summary-toggle').append(this.yearlySummaryToggle.el)
			this.yearlySummaryToggle.render()
			this.$('.yearly-summary-row').removeClass('is-not-displayed')
		}
	},

	_renderSubtotals: function() {
		this.$('.subtotals .subtotal-item').remove()
		let list = this.$('.subtotals .list')

		let notAllowSubtotals = _.isEmpty(this.breakdown.validSubtotalCalendarIds())
		let subtotalCalendars = this.breakdown.get('subtotalCalendarIds') || []
		let noSubtotalCalendars = subtotalCalendars.length == 0
		let hideSubtotals = notAllowSubtotals && noSubtotalCalendars &&
			this.reportModel.reportDomainType == 'scorecard-item'

		this.$('.subtotals').toggleClass('is-not-displayed', hideSubtotals)
		_.each(subtotalCalendars, calId => {
			list.append(subtotalListRow(calendars.calendarsById[calId]))
		})
		this.$('.subtotals .panel-content')
			.toggleClass('is-showing-empty-state', noSubtotalCalendars)
		this._renderAddSubtotal()
	},

	_renderAddSubtotal: function() {
		//if no subtotals to add, prevent add from showing
		this.$('.subtotals .add-container').toggleClass('is-not-displayed',
			_.isEmpty(this.breakdown.validSubtotalCalendarIds()))
	},

	/**
	 * renderFieldDropdown uses the VLF id, so our current value will not be
	 * loaded as the default selection
	 */
	_maybeTranslateFieldIdToVlfId: function(fieldIdOrColumnType) {
		const vlf = _.get(this.dataset, 'visibleLinkedFields', [])
			.find(it => it.fieldId == fieldIdOrColumnType)
		 return vlf ? -vlf.id : fieldIdOrColumnType
	},

	/**
	 * renderFieldDropdown returns the VLF id, so we have to get back to the
	 * field id i.e. the one known to this dataset
	 */
	_maybeTranslateVlfIdToFieldId: function(fieldIdOrVlfIdOrColumnType) {
		const vlf = this.dataset && this.dataset
			.getVisibleLinkedFieldByFieldIdentifier(fieldIdOrVlfIdOrColumnType)
		return vlf ? vlf.fieldId : fieldIdOrVlfIdOrColumnType
	},

	_onChangeFieldIdOrColumnType: function(e) {
		let fieldIdOrColumnType = this.$('select[name=fieldIdOrColumnType]').val()
		if (fieldIdOrColumnType && !_.isEmpty(fieldIdOrColumnType)) {
			if (this.breakdown.has('range')) {
				this._lastRange = this.breakdown.get('range')
			}
			let {breakdownBy, range} = this.options.reportModel
				.defaultBreakdownDataByFieldIdOrColumnType(fieldIdOrColumnType)
			if (!_.isNil(breakdownBy)) {
				breakdownBy = this._maybeTranslateVlfIdToFieldId(fieldIdOrColumnType)
			}

			const columnData = this.reportModel.columnDataByFieldIdOrColumnType(breakdownBy)
			const ranges = { range, lastRange: this._lastRange }
			const attrs = defaultBreakdownAttributes({ columnData, breakdownBy, ranges })
			this.breakdown.set(attrs)
			this._renderCatOrRangeSelector()
		}
	},

	_onChangeBreakdownBy: function(model, fieldIdOrColumnType) {
		this.fieldIdOrColumnType = fieldIdOrColumnType
		this._renderCatOrRangeSelector()
		this._renderCalendarRangeSelectorAndSubtotals()
	},

	_onChangeSummaryType: function() {
		if (!this.dataset) { return }

		let restriction = this.$('select[name=summaryType]').val()
		this.breakdown.set('summaryType', restriction)

		//send the default dataset range for this field if we need to use
		//on the server so we don't have to query for the info
		let field = this._selectedDatasetField()
		if (!_.isNil(field.ranges)) {
			this.breakdown.set('datasetCatOrRanges', field.ranges)
		}
	},

	_onChangeYearlySummary: function() {
		this.breakdown.set('yearlySummary', this.yearlySummaryToggle.val())
	},

	_onClickYearlySummaryToggle: function(e) {
		this.permissionsTooltip = new Tooltip({
			attachTo: e.target,
			content: i18n.get('report.designer.repeat.showToDate.notSupported'),
			placement: 'below',
			arrow: 'center'
		}).render()
	},

	_onClickSubtotalAdd: function(e) {
		let subtotalCalendars = _.map(this.breakdown.validSubtotalCalendarIds(),
				calId => calendars.calendarsById[calId])
		let menu = new SubtotalMenu({
			calendars : subtotalCalendars
		})
		menu.on('done', calId => {
			this.breakdown.addSubtotalCalendarId(calId)
			this._renderSubtotals()
		})

		this.addSubtotalTooltip = new Tooltip({
			attachTo: e.target,
			content: menu,
			extraClass: 'has-bootslide-menu'
		}).render()
	},

	_onClickSubtotalDelete: function(e) {
		let calId = this.$(e.target).data('id')
		this.breakdown.removeSubtotalCalendarId(calId)
		this._renderSubtotals()

		if (_.isNil(this.breakdown.validate())) {
			this.formView.clearErrors()
			this.formView.change()
		}
	},

	_selectedDatasetField: function() {
		return this.dataset.getFieldByFieldIdentifier(this.fieldIdOrColumnType)
	},

	test: function() {
		return this.breakdown.validate()
	},

	_onDone: function() {
		if (this.calendarRangeSelector) {
			//slient here because this breakdown is just a copy used for this specific view
			//we want any actual logic from the change to be happen on the model
			//attached to the view used to display the report (not just for this specific view)
			this.breakdown.set('range', this.calendarRangeSelector.val(), {silent: true})
		}

		if (this.dataset) {
			//if this is a dataset, the breakdown should point to the fieldId
			//and if present the VLF ID
			let {fieldId, visibleLinkedFieldId} = this.dataset.fieldIdentifierToIds(this.breakdown.get('breakdownBy'))
			this.breakdown.set({
				breakdownBy: fieldId,
				breakdownByVisibleLinkedFieldId: visibleLinkedFieldId
			}, {silent: true})
		}
		return this.breakdown
	},

	remove: function() {
		StackForm.prototype.remove.apply(this, arguments)
		_.invokeMap([this.calendarRangeSelector, this.yearlySummaryToggle,
				this.permissionsTooltip, this.addSubtotalTooltip,
			this.covertButtonTooltips], 'remove')
	}
})

export default RepeatByForm
export { defaultBreakdownAttributes }
export { isBreakdownByCalendarPeriod }
RepeatByForm.__filename__ = 'reports/common/repeat-by-form.js'
